import { parseISO } from 'date-fns'
import { useFormikContext } from 'formik'
import { styled } from 'styled-components'

import { useDeliveryMethodModal } from '@b-stock/fe-core/bstock-next/components'
import {
  getNumberOfUnitsInLot,
  isLotUnmanifested,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  centsToDollars,
  Button,
  FormattedMessage,
  useIsPast,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { CurrentPrice } from '@/buyer-portal/components/features/listing/ListingActionColumn/Bid/PriceDetails/CurrentPrice'
import FormattedMsrpLine from '@/buyer-portal/components/features/listing/ListingActionColumn/Bid/PriceDetails/FormattedMsrpLine'
import { ContractInfoBanner } from '@/buyer-portal/components/features/listing/ListingActionColumn/ContractInfoBanner'
import { OfferingStatusBanner } from '@/buyer-portal/components/features/listing/MakeAnOffer/OfferingStatusBanner/OfferingStatusBanner'
import { OfferingInfo } from '@/buyer-portal/components/features/listing/OfferingInfo/OfferingInfo'
import { useIsSellerUserOfferingOwner } from '@/buyer-portal/components/features/listing/useIsSellerUserOfferingOwner'
import { getEditDeliveryMethodRedirectUri } from '@/buyer-portal/helpers/shipping'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'
import { useIsMobile } from '@/buyer-portal/hooks/layout'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { BiddingCard } from '../../ui'
import { useOfferAnalytics } from '../useOfferAnalytics'
import { type MakeAnOfferFormValues } from './MakeAnOfferFormProvider'
import { SimpleOfferInput } from './SimpleOfferInput'

const TopContainer = styled.div`
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  padding: 0 2rem 1.5rem;
`

const BottomContainer = styled.div`
  padding: 1.5rem 2rem;
  gap: 1rem;
  display: flex;
  flex-direction: column;
`

const Label = styled.label`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.black};
`

const LastOfferContainer = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.black};
`

const ns = 'Listing.actionColumn.simpleOfferActionModule'

export const SimpleOfferActionModuleContent = ({
  listing,
  selectedQuote,
}: {
  listing: OrchestratedOfferingListingDetails
  selectedQuote?: OrchestratedQuote | null
}) => {
  const { contract, offering, offers, saleMetrics, status } = listing
  const { offerEndTime = '' } = offering ?? {}
  const viewCount = saleMetrics?.viewCount
  const mobileScreen = useIsMobile()

  const isEnded = useIsPast(parseISO(offerEndTime)) || status === 'COMPLETED'
  const isSellerUserOfferingOwner = useIsSellerUserOfferingOwner({ offering })
  const isServiceAccountOnly = useIsServiceAccountOnly()

  const { values } = useFormikContext<MakeAnOfferFormValues>()
  const { trackButtonClicked } = useAnalytics()
  const getOfferAnalytics = useOfferAnalytics(listing)

  const { deliveryMethodChangePending, editDeliveryMethod } =
    useDeliveryMethodModal()

  if (!offering) {
    return null
  }

  const currency = listing.currency ?? 'USD'

  const manifestUnits = getNumberOfUnitsInLot(listing.lot) ?? 0
  const msrpPrice = listing.lot.retailValue
  const isUnmanifested = isLotUnmanifested(listing.lot)
  const hasOffer = !!listing.offers?.length && listing.offers.length > 0
  const disabled = isSellerUserOfferingOwner || isServiceAccountOnly
  const hasQuote = !!selectedQuote

  return (
    <BiddingCard>
      <TopContainer>
        {hasOffer && (
          <LastOfferContainer>
            <FormattedMessage id={`${ns}.yourLastOffer`} />
          </LastOfferContainer>
        )}
        <CurrentPrice
          price={values.currentOfferAmount || values.minOfferAmount}
          units={manifestUnits}
          shouldHideNumbers={false}
          currency={currency}
          listing={listing}
        />
        {!contract && (
          <FormattedMsrpLine
            msrpPrice={msrpPrice}
            units={manifestUnits}
            isUnmanifested={isUnmanifested}
            currency={currency}
          />
        )}
        <OfferingInfo
          offerEndTime={offerEndTime}
          status={status}
          viewCount={viewCount}
        />
      </TopContainer>
      <BottomContainer>
        {isEnded ? (
          <OfferingStatusBanner
            offering={offering}
            offers={offers ?? []}
            contract={contract ?? null}
            isListingIndexed={false}
            status={status}
          />
        ) : (
          <>
            {hasQuote && (
              <>
                <Label data-testid="module-input-label">
                  <FormattedMessage id={`${ns}.label`} />
                </Label>
                <SimpleOfferInput
                  listing={listing}
                  ns={ns}
                  key={values.currentOfferAmount}
                  disabled={disabled}
                />
              </>
            )}
            <Button
              type={hasQuote ? 'submit' : 'button'}
              appearance={deliveryMethodChangePending ? 'loading' : 'primary'}
              size={mobileScreen ? 'medium' : 'large'}
              disabled={disabled || deliveryMethodChangePending}
              onClick={() => {
                if (!hasQuote) {
                  editDeliveryMethod({
                    redirectURI: getEditDeliveryMethodRedirectUri(),
                  })
                  trackButtonClicked({
                    buttonName: 'select_shipment_to_offer',
                    ...getOfferAnalytics(),
                  })
                  return
                }
                trackButtonClicked({
                  buttonName: hasOffer ? 'update_offer' : 'make_offer',
                  offer_amt_usd: centsToDollars(values.amount ?? 0),
                  ...getOfferAnalytics(),
                })
              }}
            >
              <FormattedMessage
                id={`${ns}.button.${!hasQuote ? 'noQuote' : hasOffer ? 'update' : 'create'}`}
              />
            </Button>
            {contract ? <ContractInfoBanner /> : null}
          </>
        )}
      </BottomContainer>
    </BiddingCard>
  )
}
